import React, { useCallback, useMemo } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import CheckCircleIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import HipchatChevronDownIcon from '@atlaskit/icon/glyph/hipchat/chevron-down';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { Status } from '@atlassian/jira-project-create-importer-common';
import messages from './messages';

export const useButtonVariation = (status: Status, isOpen: boolean) => {
	const { formatMessage } = useIntl();

	const getIconColor = useCallback(
		(color: string) => (isOpen ? token('color.icon.selected', '#0C66E4') : color),
		[isOpen],
	);

	return useMemo(() => {
		if (status === Status.SUCCESS) {
			return {
				title: (
					<Box xcss={[buttonTextStyles, openTextColorStyles]}>
						{formatMessage(messages.importComplete)}
					</Box>
				),
				leftIcon: (
					<CheckCircleIcon
						primaryColor={token('color.icon.selected', '#0C66E4')}
						secondaryColor={token('color.background.information', '#E9F2FF')}
						label={formatMessage(messages.importComplete)}
						size="small"
					/>
				),
				rightIcon: (
					<HipchatChevronDownIcon
						primaryColor={token('color.icon.selected', '#0C66E4')}
						label={formatMessage(messages.importComplete)}
						size="small"
					/>
				),
				ButtonWrapper: OpenedButton,
			};
		}

		if (status === Status.FAILED) {
			return {
				title: (
					<Box xcss={[buttonTextStyles, isOpen ? openTextColorStyles : completeTextColorStyles]}>
						{formatMessage(messages.importInComplete)}
					</Box>
				),
				leftIcon: (
					<ErrorIcon
						primaryColor={getIconColor(token('color.icon.inverse', '#FFFFFF'))}
						secondaryColor={
							isOpen
								? token('color.background.information', '#E9F2FF')
								: token('color.background.accent.red.bolder', '#C9372C')
						}
						label={formatMessage(messages.importInComplete)}
						size="medium"
					/>
				),
				rightIcon: (
					<HipchatChevronDownIcon
						primaryColor={getIconColor(token('color.icon.inverse', '#FFFFFF'))}
						label={formatMessage(messages.importInComplete)}
						size="small"
					/>
				),
				ButtonWrapper: isOpen ? OpenedButton : IncompleteButton,
			};
		}

		return {
			title: (
				<Box xcss={[buttonTextStyles, isOpen ? openTextColorStyles : closeTextColorStyles]}>
					{formatMessage(messages.importInProgress)}
				</Box>
			),
			leftIcon: (
				<SpinnedWrapper isOpen={isOpen}>
					<Spinner size="small" />
				</SpinnedWrapper>
			),
			rightIcon: (
				<HipchatChevronDownIcon
					primaryColor={getIconColor(token('color.icon', colors.N500))}
					label={formatMessage(messages.importInProgress)}
					size="small"
				/>
			),
			ButtonWrapper: isOpen ? OpenedButton : ProgressButton,
		};
	}, [formatMessage, getIconColor, isOpen, status]);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnedWrapper = styled.div<{ isOpen: boolean }>({
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	'--ds-icon-subtle': ({ isOpen }) =>
		isOpen ? token('color.icon.selected', '#0C66E4') : token('color.icon.subtle', '#626F86'),
	paddingRight: token('space.025', '2px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PaddingButton = styled(Button)({
	marginLeft: token('space.100', '8px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	padding: `0 ${token('space.100', '8px')} !important`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OpenedButton = styled(PaddingButton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.information', '#E9F2FF')} !important`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ProgressButton = styled(PaddingButton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.neutral', '#091E420F')} !important`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IncompleteButton = styled(PaddingButton)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	backgroundColor: `${token('color.background.accent.red.bolder', '#C9372C')} !important`,
});

const buttonTextStyles = xcss({
	fontSize: '14px',
	fontStyle: 'normal',
	fontWeight: 500,
});

const openTextColorStyles = xcss({
	color: 'color.text.brand',
});

const closeTextColorStyles = xcss({
	color: 'color.text',
});

const completeTextColorStyles = xcss({
	color: 'color.text.inverse',
});
